(() => {
  const ROOT_SELECTOR = '.kwl-personalizer';

  const PRESETS = {
    latin_basic: '\u0020-\u007E',
    latin_plus: '\u0020-\u007E\u00A0-\u00FF',
    jessica: '\u0020-\u007E\u00A0-\u00FF\u2018\u2019\u201C\u201D\u2030\u20AC\u2122\uF000-\uF014'
  };

  function q(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qa(root, sel) {
    return root ? Array.from(root.querySelectorAll(sel)) : [];
  }

  function getSectionScope(root) {
    return (
      root.closest('[data-section-id]') ||
      root.closest('.shopify-section') ||
      root.closest('product-info') ||
      root.closest('.product-section') ||
      document
    );
  }

  function getStateKey(root) {
    const section =
      root.closest('[data-section-id]')?.getAttribute('data-section-id') ||
      root.closest('.shopify-section')?.id ||
      'global';
    const rootId = root.id || root.dataset.fontFamily || 'personalizer';
    return `kwl:${section}:${rootId}`;
  }

  function loadState(root) {
    try {
      return JSON.parse(sessionStorage.getItem(getStateKey(root)) || '{}');
    } catch (e) {
      return {};
    }
  }

  function saveState(root, patch) {
    try {
      const curr = loadState(root);
      sessionStorage.setItem(getStateKey(root), JSON.stringify({ ...curr, ...patch }));
    } catch (e) {}
  }

  function buildSingleCharRegex(classBody) {
    if (!classBody) return null;
    if (classBody === '0-9') return /^[0-9]$/u;
    try {
      return new RegExp(`^[${classBody}]$`, 'u');
    } catch (e) {
      return null;
    }
  }

  function parseLimits(root) {
    const tag = q(root, 'script[data-limits]');
    if (!tag) return {};
    try {
      return JSON.parse(tag.textContent || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function getClosestProductForm(root) {
    return (
      root.closest('form[data-type="add-to-cart-form"]') ||
      root.closest('form[action*="/cart/add"]') ||
      root.closest('form.product-form') ||
      root.closest('form') ||
      document.querySelector('form[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action*="/cart/add"]') ||
      document.querySelector('form.product-form') ||
      document.querySelector('form')
    );
  }

  function ensureFormAssociation(el, form) {
    if (!el || !form) return;
    if (form.contains(el)) return;

    if (!form.id) {
      form.id = `kwl-form-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    el.setAttribute('form', form.id);
  }

  function ensureHiddenPropertyInput(form, name) {
    if (!form || !name) return null;

    let el = form.querySelector(`input[type="hidden"][name="${CSS.escape(name)}"]`);
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    return el;
  }

  function normalizeSizeLabel(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/["″]/g, 'in')
      .replace(/\binches\b/g, 'in')
      .replace(/\binch\b/g, 'in')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function findSizedLimit(limitsMap, label) {
    const normalizedLabel = normalizeSizeLabel(label);
    if (!normalizedLabel) return null;

    for (const key in limitsMap) {
      if (normalizeSizeLabel(key) === normalizedLabel) {
        const v = Number(limitsMap[key]);
        if (Number.isFinite(v)) return v;
      }
    }

    return null;
  }

  function getGroupLabel(group) {
    return (
      group.getAttribute('data-option-name') ||
      q(group, 'legend')?.textContent ||
      q(group, '.product-form__option-name')?.textContent ||
      q(group, '.product-form__label')?.textContent ||
      q(group, '.selector-wrapper label')?.textContent ||
      q(group, 'label')?.textContent ||
      ''
    ).trim();
  }

  function getSelectedFromGroup(group) {
    const checked = q(group, 'input[type="radio"]:checked');
    if (checked && checked.value) return checked.value.trim();

    const select = q(group, 'select');
    if (select && select.value) return select.value.trim();

    return '';
  }

  function getScopedSizeValue(scope, sizeNames) {
    if (!scope || !sizeNames.length) return '';

    const normalized = sizeNames.map((s) => s.toLowerCase().trim()).filter(Boolean);

    for (const sizeName of normalized) {
      const checkedRadio = scope.querySelector(`input[type="radio"][name="${CSS.escape(sizeName)}"]:checked`);
      if (checkedRadio && checkedRadio.value) return checkedRadio.value.trim();

      const checkedRadioOptions = scope.querySelector(`input[type="radio"][name="options[${CSS.escape(sizeName)}]"]:checked`);
      if (checkedRadioOptions && checkedRadioOptions.value) return checkedRadioOptions.value.trim();

      const select = scope.querySelector(`select[name="${CSS.escape(sizeName)}"]`);
      if (select && select.value) return select.value.trim();

      const selectOptions = scope.querySelector(`select[name="options[${CSS.escape(sizeName)}]"]`);
      if (selectOptions && selectOptions.value) return selectOptions.value.trim();
    }

    const groups = qa(scope, 'fieldset, .product-form__input, .product-option, .selector-wrapper, .single-option-selector');
    for (const group of groups) {
      const label = getGroupLabel(group).toLowerCase();
      if (!label) continue;
      if (!normalized.some((n) => label.includes(n))) continue;
      const value = getSelectedFromGroup(group);
      if (value) return value;
    }

    return '';
  }

  function drawIntoCanvas(ctx, text, fontFamily, fitMode, fixedPx, W, H) {
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);

    const raw = (text || '').trim() || ' ';
    const fam = fontFamily ? `"${fontFamily}", sans-serif` : 'sans-serif';

    ctx.fillStyle = '#000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    if (fitMode === 'fixed') {
      const size = fixedPx;
      ctx.font = `${size}px ${fam}`;
      const m = ctx.measureText(raw);
      const asc = m.actualBoundingBoxAscent ?? size * 0.8;
      const des = m.actualBoundingBoxDescent ?? size * 0.2;
      const glyphH = asc + des;
      const y = asc + (H - glyphH) / 2;
      ctx.fillText(raw, W / 2, y);
      return;
    }

    const pad = Math.round(Math.min(W, H) * 0.12);
    const boxW = W - 2 * pad;
    const boxH = H - 2 * pad;

    let lo = 4;
    let hi = boxH;
    for (let i = 0; i < 16; i++) {
      const mid = (lo + hi) / 2;
      ctx.font = `${mid}px ${fam}`;
      const m = ctx.measureText(raw);
      const tw = m.width;
      const asc = m.actualBoundingBoxAscent ?? mid * 0.8;
      const des = m.actualBoundingBoxDescent ?? mid * 0.2;
      const th = asc + des;
      if (tw > boxW || th > boxH) hi = mid;
      else lo = mid;
    }

    const size = lo;
    ctx.font = `${size}px ${fam}`;
    const m = ctx.measureText(raw);
    const asc = m.actualBoundingBoxAscent ?? size * 0.8;
    const des = m.actualBoundingBoxDescent ?? size * 0.2;
    const glyphH = asc + des;
    const y = pad + asc + (boxH - glyphH) / 2;
    ctx.fillText(raw, W / 2, y);
  }

  function clampToLimit(str, limit) {
    if (!str) return '';
    return Array.from(str).slice(0, limit).join('');
  }

  function initOne(root) {
    if (!root || root.dataset.kwlBound === 'true') return;
    root.dataset.kwlBound = 'true';

    const sectionScope = getSectionScope(root);
    const input = q(root, '[data-role="input"]');
    const countEl = q(root, '[data-role="count"]');
    const warnEl = q(root, '[data-role="warn"]');
    const canvas = q(root, 'canvas[data-role="preview"]');
    const ctx = canvas ? canvas.getContext('2d') : null;

    const fpBtn = q(root, '[data-role="font-button"]');
    const fpMenu = q(root, '[data-role="font-menu"]');
    const fpCurr = q(root, '[data-role="font-current"]');
    const fpItems = fpMenu ? qa(fpMenu, '[data-role="font-item"]') : [];
    const fontProp = q(root, '[data-role="font-prop"]') || q(root, 'input[type="hidden"][name="properties[Font]"]');

    if (!input) return;

    const cfg = {
      maxlen: parseInt(root.dataset.maxlen || '12', 10) || 12,
      canvasW: parseInt(root.dataset.canvasW || canvas?.width || '320', 10) || 320,
      canvasH: parseInt(root.dataset.canvasH || canvas?.height || '200', 10) || 200,
      fit: root.dataset.fit || 'scale',
      fixedPx: parseInt(root.dataset.fixedPx || '80', 10) || 80,
      allowedClass: root.dataset.allowedClass || '',
      allowedPreset: root.dataset.allowedPreset || 'latin_plus',
      sizeMode: (root.dataset.sizeMode || 'off').toLowerCase(),
      sizeNames: (root.dataset.sizeNames || 'Length|Height|Size|Sticker Size').split('|').map((s) => s.trim()).filter(Boolean)
    };

    let currentFont = (root.dataset.fontFamily || fontProp?.value || '').trim();
    let currentMax = cfg.maxlen;
    let lastWarn = '';
    let hitLimit = false;
    let composing = false;

    const limitsMap = parseLimits(root);
    const charRx = buildSingleCharRegex(cfg.allowedClass || PRESETS[cfg.allowedPreset] || PRESETS.latin_plus);

    const savedState = loadState(root);
    if (savedState.text && !input.value) input.value = savedState.text;
    if (savedState.font) {
      currentFont = savedState.font;
      root.dataset.fontFamily = savedState.font;
    }

    if (canvas) {
      canvas.width = cfg.canvasW;
      canvas.height = cfg.canvasH;
    }

    if (fpCurr && currentFont) {
      fpCurr.textContent = currentFont;
      fpCurr.style.fontFamily = `"${currentFont}"`;
    }

    if (fpItems.length && currentFont) {
      fpItems.forEach((i) => i.setAttribute('aria-selected', i.getAttribute('data-font') === currentFont ? 'true' : 'false'));
    }

    function getLiveForm() {
      return getClosestProductForm(root);
    }

    function syncFontProp() {
      const form = getLiveForm();
      if (!form) return;

      const val = currentFont || '';

      if (fontProp) {
        fontProp.value = val;
        ensureFormAssociation(fontProp, form);
      }

      let inputs = qa(form, 'input[type="hidden"][name="properties[Font]"]');

      if (fontProp && !inputs.includes(fontProp) && fontProp.form === form) {
        inputs.push(fontProp);
      }

      if (!inputs.length) {
        const created = ensureHiddenPropertyInput(form, 'properties[Font]');
        if (created) inputs = [created];
      }

      inputs.forEach((el, idx) => {
        if (idx === 0) {
          el.value = val;
          ensureFormAssociation(el, form);
        } else {
          el.remove();
        }
      });
    }

    function syncTextProp() {
      const form = getLiveForm();
      if (!form) return;

      const val = input ? (input.value || '') : '';
      let inputs = qa(form, 'input[type="hidden"][name="properties[Name]"]');

      if (!inputs.length) {
        const created = ensureHiddenPropertyInput(form, 'properties[Name]');
        if (created) inputs = [created];
      }

      inputs.forEach((el, idx) => {
        if (idx === 0) {
          el.value = val;
          ensureFormAssociation(el, form);
        } else {
          el.remove();
        }
      });
    }

    function currentLimit() {
      if (cfg.sizeMode !== 'map') {
        currentMax = cfg.maxlen;
      } else {
        const selected = getScopedSizeValue(sectionScope, cfg.sizeNames);
        const sized = findSizedLimit(limitsMap, selected);
        currentMax = Number.isFinite(sized) && sized > 0 ? sized : cfg.maxlen;
      }

      input.maxLength = currentMax;
      return currentMax;
    }

    function cleanAllowed(str) {
      if (!str) return { text: '', removed: false };

      let out = '';
      let removed = false;

      if (!charRx) return { text: str, removed: false };

      for (const ch of Array.from(str)) {
        if (charRx.test(ch)) out += ch;
        else removed = true;
      }
      return { text: out, removed };
    }

    function showWarn(html) {
      lastWarn = html || '';
      if (!warnEl) return;
      warnEl.innerHTML = html || '';
      warnEl.style.display = html ? 'block' : 'none';
    }

    function hideWarn() {
      lastWarn = '';
      if (!warnEl) return;
      warnEl.innerHTML = '';
      warnEl.style.display = 'none';
    }

    function updateCount(text, limit) {
      if (countEl) countEl.textContent = `${Array.from(text || '').length}/${limit}`;
    }

    function currentFontName() {
      return currentFont || root.dataset.fontFamily || '';
    }

    function draw(text) {
      if (!ctx || !canvas) return;
      drawIntoCanvas(ctx, text || '', currentFontName(), cfg.fit, cfg.fixedPx, canvas.width, canvas.height);
    }

    function setSanitizedValue(nextText) {
      const raw = input.value || '';
      if (nextText === raw) return;

      const start = input.selectionStart ?? raw.length;
      input.value = nextText;

      try {
        const pos = Math.min(start, nextText.length);
        input.setSelectionRange(pos, pos);
      } catch (e) {}
    }

    function performUpdate(source = 'general') {
      const form = getLiveForm();
      if (form) {
        ensureFormAssociation(input, form);
        if (fontProp) ensureFormAssociation(fontProp, form);
      }

      const limit = currentLimit();
      const raw = input.value || '';
      const allowed = cleanAllowed(raw);
      let nextText = allowed.text;
      let shrunkBySize = false;

      if (Array.from(nextText).length > limit) {
        nextText = clampToLimit(nextText, limit);
        shrunkBySize = true;
      }

      if (nextText !== raw) {
        setSanitizedValue(nextText);
      }

      updateCount(nextText, limit);
      syncTextProp();
      syncFontProp();
      draw(nextText);
      saveState(root, { text: nextText, font: currentFont });

      if (!nextText.trim()) {
        showWarn('Please enter your custom text to personalize this item.');
        return;
      }

      if (shrunkBySize) {
        hitLimit = true;
        showWarn('Please select a bigger size for more characters.');
        return;
      }

      if (allowed.removed) {
        showWarn('Some characters were removed because they aren’t supported.');
        return;
      }

      if (hitLimit && source === 'beforeinput-limit') {
        showWarn('Please select a bigger size for more characters.');
        return;
      }

      if (Array.from(nextText).length < limit && lastWarn === 'Please select a bigger size for more characters.') {
        hitLimit = false;
        hideWarn();
        return;
      }

      hideWarn();
    }

    let updateTimer = null;
    function scheduleUpdate(delay = 0, source = 'general') {
      clearTimeout(updateTimer);
      updateTimer = setTimeout(() => performUpdate(source), delay);
    }

    input.addEventListener('compositionstart', () => {
      composing = true;
    });

    input.addEventListener('compositionend', () => {
      composing = false;
      scheduleUpdate(0, 'compositionend');
    });

    input.addEventListener('beforeinput', (e) => {
      if (composing) return;

      const limit = currentLimit();
      const val = input.value || '';

      if (e.inputType && e.inputType.startsWith('delete')) {
        if (Array.from(val).length <= limit) hitLimit = false;
        return;
      }

      if (e.inputType === 'insertFromPaste') return;

      const incoming = e.data ?? '';
      if (!incoming) return;

      const cleanedIncoming = cleanAllowed(incoming).text;
      if (!cleanedIncoming && incoming) {
        e.preventDefault();
        showWarn('Some characters were removed because they aren’t supported.');
        scheduleUpdate(0, 'beforeinput-invalid');
        return;
      }

      const selStart = input.selectionStart ?? val.length;
      const selEnd = input.selectionEnd ?? val.length;
      const currentChars = Array.from(val);
      const replacingCount = Array.from(val.slice(selStart, selEnd)).length;
      const nextLen = currentChars.length - replacingCount + Array.from(cleanedIncoming).length;

      if (nextLen > limit) {
        e.preventDefault();
        hitLimit = true;
        showWarn('Please select a bigger size for more characters.');
        scheduleUpdate(0, 'beforeinput-limit');
      }
    });

    input.addEventListener('paste', (e) => {
      const data = e.clipboardData?.getData('text') ?? '';
      if (!data) return;

      e.preventDefault();

      const limit = currentLimit();
      const raw = input.value || '';
      const selStart = input.selectionStart ?? raw.length;
      const selEnd = input.selectionEnd ?? raw.length;

      const cleaned = cleanAllowed(data);
      const before = raw.slice(0, selStart);
      const after = raw.slice(selEnd);
      let merged = before + cleaned.text + after;

      let sizeTrimmed = false;
      if (Array.from(merged).length > limit) {
        merged = clampToLimit(merged, limit);
        sizeTrimmed = true;
      }

      input.value = merged;

      try {
        const pos = Math.min(Array.from(before + cleaned.text).length, merged.length);
        input.setSelectionRange(pos, pos);
      } catch (err) {}

      if (sizeTrimmed) {
        hitLimit = true;
        showWarn('Please select a bigger size for more characters.');
      } else if (cleaned.removed) {
        showWarn('Some characters were removed because they aren’t supported.');
      } else {
        hitLimit = false;
      }

      scheduleUpdate(0, 'paste');
    });

    ['input', 'change', 'focus', 'blur'].forEach((evt) => {
      input.addEventListener(
        evt,
        () => {
          if (composing) return;
          scheduleUpdate(0, evt);
        },
        { passive: true }
      );
    });

    if (fpBtn && fpMenu && fpCurr && fpItems.length) {
      fpBtn.addEventListener('click', () => {
        const open = fpMenu.getAttribute('aria-hidden') === 'false';
        fpMenu.setAttribute('aria-hidden', open ? 'true' : 'false');
        fpBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });

      fpItems.forEach((item) => {
        item.addEventListener('click', () => {
          currentFont = (item.getAttribute('data-font') || currentFont || '').trim();
          root.dataset.fontFamily = currentFont;
          fpCurr.textContent = currentFont;
          fpCurr.style.fontFamily = `"${currentFont}"`;
          fpItems.forEach((i) => i.setAttribute('aria-selected', i === item ? 'true' : 'false'));
          fpMenu.setAttribute('aria-hidden', 'true');
          fpBtn.setAttribute('aria-expanded', 'false');
          syncFontProp();
          saveState(root, { text: input.value || '', font: currentFont });

          if (document.fonts && currentFont) {
            document.fonts
              .load(`64px "${currentFont}"`)
              .then(() => scheduleUpdate(0, 'font-change'))
              .catch(() => scheduleUpdate(0, 'font-change'));
          } else {
            scheduleUpdate(0, 'font-change');
          }
        });
      });

      document.addEventListener('click', (e) => {
        if (!root.contains(e.target)) {
          fpMenu.setAttribute('aria-hidden', 'true');
          fpBtn.setAttribute('aria-expanded', 'false');
        }
      });
    }

    const sectionListener = () => scheduleUpdate(25, 'section-change');

    if (sectionScope) {
      sectionScope.addEventListener('change', sectionListener, true);
      sectionScope.addEventListener('input', sectionListener, true);
    }

    document.addEventListener('variant:change', () => scheduleUpdate(50, 'variant-event'));
    document.addEventListener('variant:changed', () => scheduleUpdate(50, 'variant-event'));
    document.addEventListener('product:variant-change', () => scheduleUpdate(50, 'variant-event'));
    document.addEventListener('shopify:section:load', () => scheduleUpdate(60, 'section-load'));
    document.addEventListener('shopify:section:reorder', () => scheduleUpdate(60, 'section-load'));
    document.addEventListener('shopify:section:select', () => scheduleUpdate(60, 'section-load'));
    document.addEventListener('page:load', () => scheduleUpdate(60, 'page-load'));

    document.addEventListener(
      'change',
      (e) => {
        if (
          e.target.closest('variant-radios') ||
          e.target.closest('variant-selects') ||
          e.target.closest('.selector-wrapper') ||
          e.target.matches('input[name^="options["]') ||
          e.target.matches('select[name^="options["]') ||
          e.target.matches('select.single-option-selector') ||
          e.target.matches('.single-option-selector')
        ) {
          scheduleUpdate(50, 'option-change');
        }
      },
      true
    );

    root._kwlRefresh = () => {
      const state = loadState(root);

      if (state.text && !input.value) input.value = state.text;
      if (state.font) {
        currentFont = state.font;
        root.dataset.fontFamily = state.font;
        if (fontProp) fontProp.value = state.font;

        if (fpCurr) {
          fpCurr.textContent = state.font;
          fpCurr.style.fontFamily = `"${state.font}"`;
        }

        if (fpItems.length) {
          fpItems.forEach((i) =>
            i.setAttribute(
              'aria-selected',
              i.getAttribute('data-font') === state.font ? 'true' : 'false'
            )
          );
        }
      }

      syncTextProp();
      syncFontProp();
      scheduleUpdate(0, 'refresh');
    };

    if (document.fonts && currentFontName()) {
      document.fonts
        .load(`64px "${currentFontName()}"`)
        .then(() => scheduleUpdate(0, 'init'))
        .catch(() => scheduleUpdate(0, 'init'));
    } else {
      requestAnimationFrame(() => scheduleUpdate(0, 'init'));
    }
  }

  function initAll(scope = document) {
    qa(scope, ROOT_SELECTOR).forEach(initOne);
  }

  function refreshAll() {
    qa(document, ROOT_SELECTOR).forEach((root) => {
      if (typeof root._kwlRefresh === 'function') root._kwlRefresh();
    });
  }

  function recheckAll() {
    initAll(document);
    refreshAll();
  }

  function recheckSoon() {
    requestAnimationFrame(() => {
      recheckAll();
      setTimeout(recheckAll, 60);
    });
  }

  window.KWLPersonalizerInit = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        recheckAll();
        setTimeout(recheckAll, 120);
        setTimeout(recheckAll, 300);
      },
      { once: true }
    );
  } else {
    recheckAll();
    setTimeout(recheckAll, 120);
    setTimeout(recheckAll, 300);
  }

  const obs = new MutationObserver((mutations) => {
    const relevant = mutations.some((m) => {
      if (!m.target) return false;
      if (m.target.nodeType !== 1) return false;
      if (m.target.closest?.(ROOT_SELECTOR)) return false;

      return (
        qa(m.target, ROOT_SELECTOR).length ||
        m.target.matches?.('.shopify-section, form, [data-section-id], .product-section') ||
        qa(
          m.target,
          'form[action*="/cart/add"], form[data-type="add-to-cart-form"], .selector-wrapper, .single-option-selector'
        ).length
      );
    });

    if (!relevant) return;
    clearTimeout(window.__kwlObsTimer);
    window.__kwlObsTimer = setTimeout(recheckSoon, 50);
  });

  obs.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target.closest(
        'form[data-type="add-to-cart-form"], form[action*="/cart/add"], form.product-form, form'
      );
      if (!form) return;

      const root =
        form.querySelector(ROOT_SELECTOR) ||
        document.querySelector(`${ROOT_SELECTOR}[data-kwl-bound="true"]`) ||
        document.querySelector(ROOT_SELECTOR);

      if (!root) return;

      const input = q(root, '[data-role="input"]');
      if (!input) return;

      let nameInput = form.querySelector('input[type="hidden"][name="properties[Name]"]');
      if (!nameInput) {
        nameInput = document.createElement('input');
        nameInput.type = 'hidden';
        nameInput.name = 'properties[Name]';
        form.appendChild(nameInput);
      }

      let fontInput = form.querySelector('input[type="hidden"][name="properties[Font]"]');
      if (!fontInput) {
        fontInput = document.createElement('input');
        fontInput.type = 'hidden';
        fontInput.name = 'properties[Font]';
        form.appendChild(fontInput);
      }

      const currentFont =
        root.dataset.fontFamily ||
        q(root, '[data-role="font-prop"]')?.value ||
        '';

      nameInput.value = input.value || '';
      fontInput.value = currentFont || '';

      const dupesName = qa(form, 'input[type="hidden"][name="properties[Name]"]');
      dupesName.forEach((el, idx) => {
        if (idx > 0) el.remove();
      });

      const dupesFont = qa(form, 'input[type="hidden"][name="properties[Font]"]');
      dupesFont.forEach((el, idx) => {
        if (idx > 0) el.remove();
      });

      console.log('[KWL SUBMIT SYNC]', {
        visible: input.value || '',
        submittedName: nameInput.value,
        submittedFont: fontInput.value
      });
    },
    true
  );
})();